/* eslint-disable @typescript-eslint/ban-ts-comment */
import { computed, reactive, onMounted, watch } from 'vue'
import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import { store } from '@remote/store'
import { useBaseStore } from '@remote/store/base'
import adapterRequest from '@remote/api/adapter'
import { HEZI_API_URL } from '@remote/api/config'
import { requestAnimationFrame } from '@remote/utils/base'
import {
  isVideoFile,
  isImageFile,
  submitDownloadFileToBroswer,
  FloderIcon,
  DefaultDownloadDirIcon,
  pathJoin,
  escapeRegex,
} from '@remote/utils/file-helper'
import {
  deleteHeziFile,
  moveFile,
  createFolder,
  shearFiles,
  copyFiles,
} from '@remote/api/adapter/shell-mqtt'
import {
  IDiskPartitionItem,
  IFile,
  IGetFilesRes,
  INetDrives,
} from '@remote/type/hezi'
import { IErrorRes } from '@remote/type'
import { getDisks, getNetDrives, globalDisk } from './disk'
import buildInDiskIcon from '@remote/assets/images/disk/hd-disk.png'
import uDiskIcon from '@remote/assets/images/disk/u-disk.png'
import { toast } from '@remote/utils/dialog'
import throttle from 'lodash/throttle'
import { poll2queryJob } from '@remote/api/adapter/helper/get-job'
const sambaIcon = 'https://oss.shumeipan.cn/hezi/images/static/web/samba.png'
const webdavIcon = 'https://oss.shumeipan.cn/hezi/images/static/web/webdav.png'

type TGetFileApiParams = {
  file_path: string
  last_page_token: string
  page_size: number
  t: number
  order_by_asc?: string
  order_by_desc?: string
}

type TGetFileApiResult = Promise<[IErrorRes, IGetFilesRes, number]>

export async function getFileApi (
  params: TGetFileApiParams,
  requestId = 0,
): TGetFileApiResult {
  const [err, res] = await adapterRequest('/vod/files', params, { apiEnv: 'heziDevelopment' })
  return [err, res, requestId]
}

type TGetAllFolderApi = {
  id: string
  title: string
  icon: string
  path: string
  is_write: true
  parent_id: string
  __order: number
  params: {}
  isDir: true
}

type TFolderPath = {
  name: string
  md5sum: string
  _md5sum: number
  showName: string
}

// 获取指定目录下全部文件夹
export async function getAllFolderApi (
  params: TGetFileApiParams,
  requestId = 0,
) {

  let list: Array<TGetAllFolderApi> = []
  let errMsg: IErrorRes | null = null
  let lastPageToken = params.last_page_token

  const getData = async (resolve) => {
    const [err, data]: [IErrorRes, IGetFilesRes] = await adapterRequest('/vod/files', Object.assign({}, params, { last_page_token: lastPageToken }), { apiEnv: 'heziDevelopment' })
    if (!err && data && Array.isArray(data.result)) {
      lastPageToken = data.pageToken
      const children = data.result
        .filter(f => f.type === 'folder')
        .map((f, index) => ({
          id: f.md5sum,
          title: f.name,
          icon: f.name === '我的下载' ? DefaultDownloadDirIcon : FloderIcon,
          path: f.path,
          is_write: true,
          parent_id: f.md5sum,
          __order: index,
          params: {},
          isDir: true,
        })) as Array<TGetAllFolderApi>
      list = list.concat(children)
      if (children.length >= params.page_size) {
        requestAnimationFrame(getData.bind(null, resolve))
      } else {
        resolve()
      }
    } else {
      errMsg = err
      resolve()
    }
  }

  const loop = () => {
    return new Promise((resolve) => {
      getData(resolve)
    })
  }

  await loop()
  return [errMsg, list, requestId]
}

type TGetFileInfoApiParams = {
  space: 'hezi'
  id: string
  usage: 'PLAY' | 'FETCH'
  theme: 'dark'
  scene: 'heziFile'
  path: string
  size: string
  hash: string
}

type TGetFileInfoApiResult = Promise<[IErrorRes, IFile]>

export async function getFileInfoApi (
  params: TGetFileInfoApiParams,
): TGetFileInfoApiResult {
  const [err, res] = await adapterRequest(`${HEZI_API_URL}/drive/v2/files/${params.id}`, params, { apiEnv: 'heziDevelopment' })
  return [err, res]
}

export function isSupportPreview (fileName) {
  return isImageFile(fileName) || isVideoFile(fileName)
}

export const previewFile = throttle(function viewFile (file) {
  if (isSupportPreview(file.name)) {
    store.dispatch('getFileInfo', {
      space: 'hezi',
      id: file.md5sum,
      usage: 'PLAY',
      theme: 'dark',
      scene: 'heziFile',
      apiUrl: '/drive/v2/files',
      path: file.path,
      size: String(file.size),
      resolution: file.resolution,
      hash: file.hash,
      mime_type: `video/${file.ext}`,
      name: file.name,
    })
  } else {
    toast({ type: 'warn', message: '暂不支持打开' })
  }
}, 2000, {
  trailing: false,
})

type TDirType = '' | 'disk' | 'Samba' | 'WebDAV'
// 标记操作删除的文件id
const markDeletedMap: { [md5sum: string]: boolean } = {}

export const initData = {
  all: {} as { [md5sum: string]: IFile }, // 缓存文件
  caches: {} as { [path: string]: string[] }, // 根据 md5sum来缓存对应的文件md5sum
  files: [] as string[],
  fileNums: 0 as number,
  disks: [] as IDiskPartitionItem[],
  showSelectDiskPanel: false,
  next_page_token: '',
  currentParentId: '',
  currentPath: '',
  breadcrumbs: [
    {
      name: '全部文件',
      md5sum: '',
      _md5sum: 0,
      showName: '全部文件',
    },
  ] as TFolderPath[],
  isLoading: true,
  requestId: 0,
  root: '',
  diskName: '内置磁盘',
  dirType: '' as TDirType,
  selecteds: [] as string[],
  selectedAll: false,
  errorMsg: '',
  isCreateFolderAction: false,
  sortParams: {
    order_by_asc: '',
    order_by_desc: '',
  },
}

export const useHeziFileStore = defineStore('heziFile', () => {
  const data = reactive(initData)
  const isFullScreen = computed(() => store.getters.isFullScreen)

  /**
  * @function 获取文件列表
  * @description 通过文件md5sum换取文件path或者通过path查询文件列表,优先通过md5sum换取path
  * @param {String} md5sum - 字符串 文件md5sum
  * @return {String} path - 字符串 文件路径
  * @return {String} caller - 字符串 function调用者,用于调试
  * @author lushaoqing
  * @version 1.0.0
  */
  const getFiles = async (md5sum = '', path?: string, caller?: string) => {
    console.log('caller', caller, md5sum)
    data.requestId += 1
    data.isLoading = true
    data.errorMsg = ''

    const nextPageToken = data.next_page_token
    // 将磁盘列表转换成文件列表
    const mapDiskToFile = (disk: IDiskPartitionItem): string => {
      const item = {
        name: disk.full_display_name,
        type: 'disk',
        md5sum: disk.uuid,
        icon_link: disk.removable ? uDiskIcon : buildInDiskIcon,
        path: disk.mount,
        size: disk.size,
        used_size: disk.size - disk.free,
        ut: '',
      }
      // @ts-ignore
      data.all[item.md5sum] = item as IFile
      return item.md5sum
    }
    // 将外挂存储转换成文件列表
    const mapNetDriveToFile = (disk: INetDrives): string => {
      const item = {
        name: disk.url || disk.path,
        type: disk.type,
        md5sum: disk.id,
        icon_link: disk.type === 'WebDAV' ? webdavIcon : sambaIcon,
        path: disk.mount,
        size: '',
        ut: disk.created_at,
        errno: disk.errno,
      }
      // @ts-ignore
      data.all[item.md5sum] = item as IFile
      return item.md5sum
    }

    // breadcrumbs 的长度未1则为第一级磁盘目录
    const isRootDirectory = data.breadcrumbs.length === 1
    if (isRootDirectory) {
      data.dirType = ''
      if (data.disks) {
        const folders = data.disks.map(mapDiskToFile)
        data.files = folders.slice()
      }

      const netDrives = globalDisk.netDrives || await getNetDrives()
      console.log('netDrives', netDrives)
      let getDisksRes
      if (globalDisk.disks?.length) {
        getDisksRes = Promise.resolve({ ...globalDisk, requestId: data.requestId })
      } else {
        getDisksRes = getDisks(data.requestId)
      }
      const { root, disks, builtInDisk, requestId } = await getDisksRes
      if (data.requestId !== requestId) return
      data.root = root
      data.disks = disks
      data.diskName = builtInDisk?.display_name || ''
      if (!builtInDisk) data.diskName = disks[0]?.display_name || ''
      const folders = disks.map(mapDiskToFile)
      console.log('netDrives', netDrives)
      data.files = folders.slice().concat(netDrives.map(mapNetDriveToFile))
      data.next_page_token = ''
      data.isLoading = false
      console.log('folders', folders, data.all)
      return
    }

    const isSecondDirectory = data.breadcrumbs.length === 2
    const dirInfo = data.all[md5sum]

    if (isSecondDirectory) {
      data.dirType = dirInfo?.type as TDirType
    }

    if (isSecondDirectory && !(data.all[md5sum]?.path || path)) {
      const errorMsg = dirInfo?.type === 'netDrive' ? `发生错误\n当前外部设备出现断链或其他异常，暂时无法访问。\n 错误码${dirInfo.errno || -1}` : '磁盘未挂载'
      data.isLoading = !!path
      data.files = []
      data.errorMsg = errorMsg
      return
    }

    // const prevPath = data.currentPath
    data.currentPath = data.all[md5sum]?.path || path || data.root
    data.files = data.caches[data.currentPath] || []

    const [err, res, requestId]: [IErrorRes, IGetFilesRes, number] = await getFileApi(
      {
        file_path: encodeURIComponent(data.currentPath),
        last_page_token: nextPageToken || '',
        page_size: 50,
        t: Date.now(),
        ...data.sortParams,
      },
      data.requestId,
    )

    if (data.requestId !== requestId) return

    if (err && data.root) {
      data.files = []
      data.next_page_token = ''
      data.isLoading = false
      data.errorMsg = err.error_description
      return toast({ type: 'error', message: err.error_description })
    }


    if (res?.result?.length) {
      const files = res.result.map((v) => {
        data.all[v.md5sum] = v
        return v.md5sum
      })

      const mergeFiles = nextPageToken ? data.files.concat(files) : files
      const nextFiles = mergeFiles.filter(id => !markDeletedMap[id])
      data.caches[data.currentPath] = nextFiles
      data.files = nextFiles
      data.next_page_token = res.pageToken
      data.fileNums = res.file_nums - (mergeFiles.length - nextFiles.length)
      console.log('this.next_page_token', data.next_page_token, nextPageToken, data.fileNums)
    } else {
      console.log('getFiles', res?.result)
      // if (prevPath !== data.currentPath) 
      data.files = []
      data.next_page_token = ''
      data.caches[data.currentPath] = []
      data.fileNums = 0
    }
    data.isLoading = false
  }
  // 文件store数据初始化
  const init = () => {
    data.currentParentId = ''
    data.breadcrumbs = [
      {
        name: '全部文件',
        md5sum: '',
        _md5sum: 0,
        showName: '全部文件',
      },
    ]
    getFiles('', '', 'init')
  }
  // 文件列表翻页
  const loadMore = () => {
    console.log('loadMore', data.next_page_token)
    if (data.next_page_token) getFiles(data.currentParentId, '', 'loadMore')
  }
  // 下拉磁盘列表选中磁盘并展示文件列表
  const handelSelectDisk = (path: string, diskName: string) => {
    console.log('path', path)
    data.isLoading = !!path
    data.files = []
    data.next_page_token = ''
    data.currentParentId = ''
    data.breadcrumbs = [
      {
        name: '全部文件',
        md5sum: '',
        _md5sum: 0,
        showName: '全部文件',
      },
    ]
    data.diskName = diskName
    data.root = path
    if (path) {
      getFiles('', path, 'handelSelectDisk')
    } else {
      data.errorMsg = `${diskName}-磁盘未挂载`
      console.log('path', data.errorMsg)
    }
    data.showSelectDiskPanel = false
  }
  // 显示或隐藏选择磁盘面板
  const toggleShowSelectDiskPanel = () => {
    data.showSelectDiskPanel = !data.showSelectDiskPanel
  }
  // 隐藏选择磁盘面板
  const hideSelectDiskPanel = () => {
    data.showSelectDiskPanel = false
  }
  // 将当前文件设置到面包屑数据
  const setBreadcrumbs = (file: IFile) => {
    const breadcrumbs = data.breadcrumbs
    const ret = breadcrumbs.filter((elm) => {
      return file.md5sum === elm.md5sum
    })
    if (ret.length) {
      breadcrumbs.splice(ret[0]._md5sum + 1, breadcrumbs.length - 1)
      data.breadcrumbs = breadcrumbs
    } else {
      breadcrumbs.push({
        md5sum: file.md5sum,
        name: file.name,
        showName: file.name.length > 9 ? `${file.name?.slice(0, 9)}...` : file.name,
        _md5sum: breadcrumbs.length,
      })
      data.breadcrumbs = breadcrumbs
    }
  }

  // 触发新建文件夹
  const toggleVisibleCreateFolder = (visible?: boolean) => {
    if (visible !== void 0) {
      data.isCreateFolderAction = visible
    } else {
      data.isCreateFolderAction = !data.isCreateFolderAction
    }
  }

  // 重置文件选择项
  const resetSeletedFile = (md5sum?: string) => {
    data.selectedAll = false
    if (md5sum && data.all[md5sum]) {
      data.selecteds = data.selecteds.filter(id => id !== md5sum)
      data.all[md5sum].checkType = 0
    } else {
      const selecteds = data.selecteds.slice()
      data.selecteds = []
      selecteds.forEach(id => {
        if (data.all[id]) data.all[id].checkType = 0
      })
    }
  }

  // 监听面包屑变化
  const onBreadCrumbChange = (switchedFolder?: TFolderPath) => {
    console.log('onBreadCrumbChange', switchedFolder)
    toggleVisibleCreateFolder(false)
    resetSeletedFile()
    // 切换至未挂载的磁盘不做响应 - 已去除限制，目前支持外部存储
    // if (!data.root) return
    const folder = switchedFolder || data.breadcrumbs.at(-2) as TFolderPath
    data.next_page_token = ''
    // 跳转至磁盘根目录
    if (folder.md5sum === '' && folder._md5sum === 0) {
      if (data.breadcrumbs.length === 1) toggleShowSelectDiskPanel()
      return init()
    }
    data.showSelectDiskPanel = false
    try {
      const file = data.all[folder.md5sum] || {}
      data.currentParentId = file.md5sum
      setBreadcrumbs(file)
      getFiles(data.currentParentId, '', 'onBreadCrumbChange')
    } catch (err) {
      console.log('', err)
    }
  }

  // 刷新当前文件列表
  const refreshFiles = () => {
    if (globalDisk.disks) data.disks = globalDisk.disks
    // 切换至未挂载的磁盘不做响应 - 已去除限制，目前支持外部存储
    // if (!data.root) return
    // if (globalDisk.root) data.root = globalDisk.root
    getFiles(data.currentParentId, '', 'refresh')
    resetSeletedFile()
  }

  // 设置或取消文件选择项
  interface IHandleSeletedFileParams {
    md5sum: string
    singleSeleted?: boolean
    caller: string
    index?: number
    shiftKey?: boolean
    ctrlKey?: boolean
  }

  const handleSeletedFile = (params: IHandleSeletedFileParams) => {
    const { md5sum, singleSeleted = false, caller = '', index: endIndex, shiftKey } = params
    const index = data.selecteds.findIndex(id => id === md5sum)
    console.log('handleSeletedFile 1:', index, data.all[md5sum].name, caller, singleSeleted, endIndex)
    // 按住shift键批量选择
    if (shiftKey && data.selecteds.length > 0 && endIndex !== void 0) {
      const startId = data.selecteds[0]
      const startIndex = data.files.findIndex(id => id === startId)

      for (const md5sum of data.selecteds) {
        data.all[md5sum].checkType = 0
      }

      if (endIndex > startIndex) {
        data.selecteds = data.files.slice(startIndex, endIndex + 1)
      } else {
        data.selecteds = data.files.slice(endIndex, startIndex + 1).reverse()
      }

      for (const md5sum of data.selecteds) {
        data.all[md5sum].checkType = 1
      }
      // 右键单选
    } else if (singleSeleted) {
      resetSeletedFile()
      data.selecteds = [md5sum]
      data.all[md5sum].checkType = 1
    } else {
      if (index >= 0) {
        data.selecteds.splice(index, 1)
        data.all[md5sum].checkType = 0
      } else {
        data.selecteds.push(md5sum)
        data.all[md5sum].checkType = 1
      }
    }

    data.selectedAll = !!data.selecteds.length && !!data.files.length && data.selecteds.length === data.files.length
  }
  // 全选文件列表
  const handleSeletedAllFiles = () => {
    if (data.selectedAll) {
      data.selectedAll = false
      resetSeletedFile()
    } else {
      const ids = data.files.slice()
      data.selectedAll = true
      data.selecteds = ids
      for (const md5sum of ids) {
        data.all[md5sum].checkType = 1
      }
    }
  }
  // 删除文件列表中的文件
  const handleDeleteFiles = async (md5sum: string, isDeleteFile: boolean) => {
    let willDeletedIds: string[] = []
    if (md5sum) {
      // 单项删除
      willDeletedIds.push(md5sum)
    } else {
      // 批量删除
      willDeletedIds = data.selecteds.slice()
    }
    try {
      console.log('handleDeleteFiles', md5sum, willDeletedIds, isDeleteFile)
      const willDeletedPaths: string[] = []
      for (const id of willDeletedIds) {
        willDeletedPaths.push(data.all[id].path)
        markDeletedMap[id] = true
      }
      await deleteHeziFile(willDeletedPaths, isDeleteFile)
      const nextFiles = data.files.filter(id => !markDeletedMap[id])
      data.caches[data.currentPath] = nextFiles
      data.files = nextFiles
      data.fileNums = data.fileNums - willDeletedPaths.length
      resetSeletedFile(md5sum)
    } catch (err) {
      return err
    }
  }
  // 文件重命名
  const handleRenameFile = async (md5sum: string, newName: string) => {
    const targetFile = data.all[md5sum]
    const srcPath = targetFile.path
    const dstPath = srcPath.replace(RegExp(`/${escapeRegex(targetFile.name)}$`), `/${newName}`)
    console.log('dstPath', srcPath, targetFile.name, dstPath, newName)
    try {
      await moveFile(srcPath, dstPath)
      data.all[md5sum] = Object.assign(
        {},
        targetFile, {
          name: newName,
          path: dstPath,
        })
    } catch (err) {
      return err
    }
  }
  // 获取当前目录下的文件
  const getFilesInFolder = (md5sum: string) => {
    data.next_page_token = ''
    data.showSelectDiskPanel = false
    const file = data.all[md5sum] || {}
    data.currentParentId = file.md5sum

    if (file.type == 'file') {
      return previewFile(file)
    }

    setBreadcrumbs(file)
    resetSeletedFile()
    getFiles(data.currentParentId, '', 'getFilesInFolder')
  }

  // 新建文件夹
  const handleCreateFolder = async (md5sum: string, name: string) => {
    const path = pathJoin(data.currentPath, name)
    const parentFile = data.all[data.currentParentId]
    try {
      await createFolder(path)
      data.all[path] = Object.assign(
        {},
        parentFile,
        {
          name: name,
          path: path,
          ut: Date.now(),
          md5sum: path,
          size: 0,
          icon_url: 'https://backstage-img-ssl.a.88cdn.com/36de21fd06b9ebdd5092b68652b6fc6118e23c2b',
        })
      data.files.unshift(path)
      data.fileNums = data.fileNums + 1
    } catch (err) {
      return err
    }
  }

  const handleCutOrCopFile = async (dstPath: string, ids: string[], action: 'cut' | 'copy') => {
    const groupId = uuidv4()
    const paths: string[] = []
    const markMovedMap = {}

    for (const id of ids) {
      paths.push(data.all[id].path)
      if (action === 'cut') markMovedMap[id] = true
    }

    const params = {
      src_paths: paths,
      dst_path: dstPath,
      rename: true,
      group_id: groupId,
    }

    try {
      if (action === 'copy') {
        await copyFiles(params)
      } else {
        await shearFiles(params)
        data.files = data.files.filter(id => !markMovedMap[id])
        data.fileNums = data.fileNums - Object.keys(markMovedMap).length
      }
      // 更新剪切或者移动后store数据
      resetSeletedFile(ids.length > 1 ? '' : ids[0])
      const queryJobParams = {
        biz: 'copy',
        group_id: groupId,
        status: 100,
        device_id: store.state.curDeviceRunner?.target,
        page_nums: 1,
        options: {
          timeout: 0,
          interval: 3 * 1000,
          leading: true,
          onCheck: (jobs) => {
            const [job] = jobs
            return ['', [1, 5].includes(job.status)]
          },
        },
      }
      // @ts-ignore
      await poll2queryJob(queryJobParams, {
        method: 'POST',
        apiEnv: 'heziProduction',
        headers: { Target: store.state.curDeviceRunner?.target },
      })
    } catch (err) {
      return err
    }
  }

  // 文件排序
  const toggleSortFiles = (key: 'time' | 'name' | 'size') => {
    if (key === data.sortParams.order_by_asc) {
      data.sortParams.order_by_asc = ''
      data.sortParams.order_by_desc = key
    } else {
      data.sortParams.order_by_asc = key
      data.sortParams.order_by_desc = ''
    }
    refreshFiles()
  }

  const baseStore = useBaseStore()
  const target = computed(() => {
    return store.state.curDeviceRunner?.target
  })

  watch(target, async (val) => {
    if (val) {
      init()
      console.log('store.state.user', store.state.Users)
    }
  })

  onMounted(async () => {
    await baseStore.untilTargetReady()
    init()
    console.log('store.state.user', store.state.user)
  })

  return {
    init,
    data,
    isFullScreen,
    getFiles,
    loadMore,
    handelSelectDisk,
    toggleShowSelectDiskPanel,
    hideSelectDiskPanel,
    onBreadCrumbChange,
    getFilesInFolder,
    refreshFiles,
    setBreadcrumbs,
    handleSeletedFile,
    resetSeletedFile,
    handleSeletedAllFiles,
    handleDeleteFiles,
    handleRenameFile,
    toggleVisibleCreateFolder,
    handleCreateFolder,
    handleCutOrCopFile,
    toggleSortFiles,
  }
})

const xhrDownload = (
  url: string,
  fileName: string,
  onProgress?: (evt: ProgressEvent<XMLHttpRequestEventTarget>) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {

    const download = (url: string, callback: (blob: Blob) => void) => {
      const request = new XMLHttpRequest()
      const onLoad = () => {
        callback(request.response)
      }

      request.responseType = 'blob'
      request.open('GET', url)

      request.addEventListener('error', reject)
      if (onProgress) request.addEventListener('progress', onProgress)
      request.addEventListener('load', onLoad)
      request.send()
    }

    const saveToLocal = (blob: Blob, fileName: string) => {
      const a = document.createElement('a')
      const url = URL.createObjectURL(blob)
      a.href = url
      a.download = fileName
      a.click()
      URL.revokeObjectURL(url)
    }

    const onCompelte = (blob: Blob) => {
      saveToLocal(blob, fileName)
      resolve()
    }

    download(url, onCompelte)
  })
}

export function downloadHeziFile (file: Pick<IFile, 'md5sum' | 'path' | 'size' | 'hash' | 'name'>, byXHR?: boolean) {
  return getFileInfoApi({
    space: 'hezi',
    id: file.md5sum,
    usage: 'FETCH',
    theme: 'dark',
    scene: 'heziFile',
    path: file.path,
    size: String(file.size),
    hash: file.hash as string,
  }).then(res => {
    const [err, data] = res
    if (err || !data?.web_content_link) {
      toast({ type: 'error', message: err.error_description || '文件下载失败' })
    } else {
      return byXHR ? xhrDownload(data?.web_content_link, file.name) : submitDownloadFileToBroswer(data?.web_content_link, file.name)
    }
  })
}

export function getFileInfoToPreview (file: Pick<IFile, 'md5sum' | 'path' | 'hash' | 'size'>) {
  return getFileInfoApi({
    space: 'hezi',
    id: file.md5sum,
    usage: 'PLAY',
    theme: 'dark',
    scene: 'heziFile',
    path: file.path,
    size: String(file.size),
    hash: file.hash as string,
  }).then(res => {
    const [err, data] = res
    if (err || !data?.web_content_link) {
      return null
    } else {
      return data
    }
  })
}

export async function getCinemaInfo (
  id: string,
): Promise<IFile | null> {
  const [err, data] = await adapterRequest(
    `${HEZI_API_URL}/drive/v1/files/${id}`, 
    {space: store.state.curDeviceRunner?.target, usage: 'PLAY'}, 
    { apiEnv: 'heziDevelopment' },
  )
  if (err || !data?.web_content_link) {
    return null
  } else {
    return data
  }
}